/**
 * EmailThreadList Component
 *
 * Displays a list of email threads with subject, date, and message count.
 */

import { Mail, ChevronRight } from 'lucide-react';
import { EmptyState, Skeleton, StatusPill, cn, formatVor } from '@elbklar/ui';

interface EmailThread {
  id: string;
  subject: string;
  gmail_thread_id: string | null;
  customer_id: string | null;
  contact_id: string | null;
  last_message_at: string | null;
  message_count: number;
  is_archived: boolean;
  created_at: string;
}

interface EmailThreadListProps {
  threads: EmailThread[];
  isLoading?: boolean;
  onSelectThread: (threadId: string) => void;
  selectedThreadId?: string | null;
}

function ThreadSkeleton() {
  return (
    <div className="border-b border-border p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Skeleton className="mb-2 h-5 w-3/4" />
          <Skeleton className="h-4 w-1/4" />
        </div>
        <Skeleton className="h-4 w-16" />
      </div>
    </div>
  );
}

export function EmailThreadList({
  threads,
  isLoading = false,
  onSelectThread,
  selectedThreadId,
}: EmailThreadListProps) {
  if (isLoading) {
    return (
      <div>
        {[1, 2, 3, 4, 5].map((i) => (
          <ThreadSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <EmptyState
        icon={Mail}
        title="Keine E-Mails vorhanden"
        description="E-Mails werden automatisch synchronisiert, sobald Gmail verbunden ist."
      />
    );
  }

  return (
    <div className="divide-y divide-border">
      {threads.map((thread) => {
        const gewaehlt = selectedThreadId === thread.id;
        return (
          <div
            key={thread.id}
            role="link"
            tabIndex={0}
            aria-current={gewaehlt ? 'true' : undefined}
            onClick={() => onSelectThread(thread.id)}
            onKeyDown={(e) => e.key === 'Enter' && onSelectThread(thread.id)}
            className={cn('cursor-pointer p-4 transition-colors hover:bg-accent', gewaehlt && 'bg-highlight-soft hover:bg-highlight-soft')}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className={cn('truncate font-medium', gewaehlt && 'text-highlight')}>{thread.subject || '(Kein Betreff)'}</p>
                <div className="mt-1 flex items-center gap-3 t-small text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Mail size={14} strokeWidth={1.75} aria-hidden />
                    {thread.message_count} {thread.message_count === 1 ? 'Nachricht' : 'Nachrichten'}
                  </span>
                  {thread.is_archived && <StatusPill tone="neutral">Archiviert</StatusPill>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2 t-small text-muted-foreground">
                {thread.last_message_at && <span>{formatVor(thread.last_message_at)}</span>}
                <ChevronRight size={16} strokeWidth={1.75} aria-hidden />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default EmailThreadList;
